import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { unwrapApiResponse } from "../../util/util";
import {
  loginUser,
  logoutUser,
  registerUser,
  saveAddress,
  resetUserPassword,
  fetchAddresses
} from "./userAPI";
import { authUtils } from "../../util/AuthUtils";

const initialState = {
  status: "idle",
  userPayload: {},
  uid: null,
  registrationError: "",
  loginError: "",
  passwordResetError: "",
  customerAddresses: [],
  fetchAddressStatus: "idle",
  fetchAddressError: "idle",
  saveAddressStatus: "idle",
  selectedAddress: {}
};

export const login = createAsyncThunk(
  "user/login",
  async ({ email, password }) => {
    try {
      return await loginUser(email, password);
    } catch (error) {
      throw new Error("Login failed. Please try again.");
    }
  }
);

export const registration = createAsyncThunk(
  "user/register",
  async ({ email, password, name, mobile, flatNo, selectedLocation }) => {
    return await registerUser(
      email,
      password,
      name,
      mobile,
      flatNo,
      selectedLocation
    );
  }
);

export const logout = createAsyncThunk("user/logout", async () => {
  return await logoutUser();
});

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async (email) => {
    return await resetUserPassword(email);
  }
);

export const getUserAddresses = createAsyncThunk(
  "user/getUserAddresses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAddresses();
      return unwrapApiResponse(response, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch addresses");
    }
  }
);

export const saveCustomerAddress = createAsyncThunk(
  "locations/saveCustomerAddress",
  async (address) => {
    return await saveAddress(address);
  }
);

export const initializeUser = createAsyncThunk(
  "user/initializeUser",
  async (_, { rejectWithValue }) => {
    const token = authUtils.getAccessToken();
    if (!token) {
      return rejectWithValue("No token");
    }
    try {
      const profile = await authUtils.getUserProfile();
      return {
        uid: profile.user?.userId || profile.userId,
        profile: profile.user || profile
      };
    } catch (error) {
      return rejectWithValue("Invalid token");
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    refreshUserId: (state, action) => {
      state.uid = action.payload.uid;
    },
    clearError: (state) => {
      state.registrationError = "";
      state.loginError = "";
      state.passwordResetError = "";
    },
    selectAddress: (state, action) => {
      state.selectedAddress = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload.uid) {
          state.uid = action.payload.uid;
          state.userPayload = action.payload;
          state.loginError = "";
        } else {
          state.loginError = action.payload;
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loginError = "LOGIN_ERROR";
      })
      .addCase(registration.pending, (state) => {
        state.status = "loading";
      })
      .addCase(registration.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload.uid) {
          state.uid = action.payload.uid;
          state.userPayload = action.payload;
          state.registrationError = "";
        } else {
          state.registrationError = action.payload;
        }
      })
      .addCase(logout.pending, (state) => {
        state.status = "loading";
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = "idle";
        state.uid = null;
        state.userPayload = {};
        state.customerAddresses = [];
        state.selectedAddress = {};
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.passwordResetError = action.payload;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.passwordResetError = action.payload;
      })
      .addCase(getUserAddresses.pending, (state, action) => {
        state.fetchAddressStatus = "pending";
        state.fetchAddressError = "";
      })
      .addCase(getUserAddresses.fulfilled, (state, action) => {
        state.fetchAddressStatus = "idle";
        state.fetchAddressError = "";
        state.customerAddresses = action.payload;
      })
      .addCase(getUserAddresses.rejected, (state, action) => {
        state.fetchAddressStatus = "idle";
        state.fetchAddressError = "Error fetching address";
      })
      .addCase(saveCustomerAddress.pending, (state) => {
        state.newAddressStatus = "pending";
        state.customerAddresses = [];
        state.selectedAddress = {};
      })
      .addCase(saveCustomerAddress.fulfilled, (state, action) => {
        state.saveAddressStatus = "fulfilled";
        state.customerAddresses = action.payload.address;
        state.selectedAddress = action.payload.address[0];
      })
      .addCase(initializeUser.pending, (state) => {
        state.status = "loading";
      })
      .addCase(initializeUser.fulfilled, (state, action) => {
        state.status = "idle";
        if (action.payload.uid) {
          state.uid = action.payload.uid;
          state.userPayload = action.payload.profile;
        }
      })
      .addCase(initializeUser.rejected, (state, action) => {
        state.status = "idle";
        state.loginError = action.payload;
      });
  }
});

export const uid = (state) => state.user.uid;
export const userPayload = (state) => state.user.userPayload;
export const customerAddresses = (state) => state.user.customerAddresses;
export const registrationError = (state) => state.user.registrationError;
export const loginError = (state) => state.user.loginError;
export const fetchAddressStatus = (state) => state.user.fetchAddressStatus;
export const saveAddressStatus = (state) => state.user.saveAddressStatus;
export const selectedAddress = (state) => state.user.selectedAddress;
export const passwordResetError = (state) => state.user.passwordResetError;
export const { refreshUserId, clearError, selectAddress } = userSlice.actions;
export default userSlice.reducer;
